import React, {useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {Cancel, Delete, Save} from '@mui/icons-material';
import {IEditArbeitszeiteintragProps} from './types';

const EditArbeitszeiteintrag: React.FC<IEditArbeitszeiteintragProps> = ({
    arbeitszeiteintrag,
    updateArbeitszeiteintrag,
    onCancel,
    onDelete,
    bauvorhabenList,
}) => {
    const [beginn, setBeginn] = useState(arbeitszeiteintrag.BEGINN);
    const [ende, setEnde] = useState(arbeitszeiteintrag.ENDE);
    const [pause, setPause] = useState(arbeitszeiteintrag.PAUSE);
    const [kurzinfo, setKurzinfo] = useState(arbeitszeiteintrag.KURZINFO || '');
    const [taetigkeit, setTaetigkeit] = useState(arbeitszeiteintrag.TAETIGKEIT || '');
    const [selectedBvhId, setSelectedBvhId] = useState(arbeitszeiteintrag.bvh);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const calculateDuration = () => {
        try {
            if (!beginn || !ende) return arbeitszeiteintrag.DAUER;

            const [startHours, startMinutes] = beginn.split(':').map(Number);
            const [endHours, endMinutes] = ende.split(':').map(Number);

            let durationHours = endHours - startHours;
            let durationMinutes = endMinutes - startMinutes;

            if (durationMinutes < 0) {
                durationHours -= 1;
                durationMinutes += 60;
            }
            return durationHours + durationMinutes / 60 - (pause || 0);
        } catch (e) {
            return arbeitszeiteintrag.DAUER;
        }
    };

    const handleSave = () => {
        const datumParts = arbeitszeiteintrag.DATUM.split('.');
        const datumFormatted = `${datumParts[2]}-${datumParts[1]}-${datumParts[0]}`;

        const beginTimestamp = `${datumFormatted}T${beginn}:00`;
        const endTimestamp = `${datumFormatted}T${ende}:00`;

        const pauseInSeconds = Math.round(pause * 3600);
        const durationInSeconds = Math.round(calculateDuration() * 3600);

        const selectedBauvorhaben = bauvorhabenList.find(bvh => bvh.id === selectedBvhId);

        const updatedEntry = {
            bearbeiter: arbeitszeiteintrag.BEARBEITER,
            beginn: beginTimestamp,
            bvh: {
                id: selectedBvhId,
                bvh_name: selectedBauvorhaben?.bvh_name || arbeitszeiteintrag.BVH,
                mandnr: selectedBauvorhaben?.mandnr || arbeitszeiteintrag.MANDNR || 0,
                projektnr: selectedBauvorhaben?.projektnr || arbeitszeiteintrag.PROJEKTNR || 0,
                bvh_uuid: selectedBauvorhaben?.bvh_uuid || arbeitszeiteintrag.bvh_uuid || '',
            },
            datum: datumFormatted,
            dauer: durationInSeconds,
            ende: endTimestamp,
            kurzinfo: kurzinfo,
            notiz: arbeitszeiteintrag.NOTIZ || '',
            pause: pauseInSeconds,
            taetigkeit: taetigkeit,
            id: arbeitszeiteintrag.id,
        };

        console.log('Updated entry to submit:', updatedEntry);
        updateArbeitszeiteintrag(updatedEntry);
    };

    const handleDelete = () => {
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        setDeleteModalOpen(false);
        onDelete(arbeitszeiteintrag.id);
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
    };

    return (
        <>
            <TableRow key={`edit-${arbeitszeiteintrag.id}`}>
                <TableCell component='th' scope='row'>
                    <FormControl size='small' fullWidth sx={{mb: 1}}>
                        <InputLabel id={`bauvorhaben-label-${arbeitszeiteintrag.id}`}>Bauvorhaben</InputLabel>
                        <Select
                            labelId={`bauvorhaben-label-${arbeitszeiteintrag.id}`}
                            value={selectedBvhId}
                            label='Bauvorhaben'
                            onChange={(e) => setSelectedBvhId(Number(e.target.value))}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200,
                                        width: 300,
                                    },
                                },
                            }}>
                            {bauvorhabenList.map((bvh) => (
                                <MenuItem key={bvh.id} value={bvh.id}>
                                    <Typography variant='body2' sx={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.2,
                                    }}>
                                        {bvh.bvh_name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        label='Beginn'
                        value={beginn}
                        onChange={(e) => setBeginn(e.target.value)}
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        label='Ende'
                        value={ende}
                        onChange={(e) => setEnde(e.target.value)}
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        type='number'
                        label='Dauer'
                        value={calculateDuration().toFixed(2)}
                        disabled
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        type='number'
                        label='Pause'
                        value={pause}
                        onChange={(e) => setPause(parseFloat(e.target.value))}
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        label='Tätigkeit'
                        value={taetigkeit}
                        onChange={(e) => setTaetigkeit(e.target.value)}
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <TextField
                        size='small'
                        label='Kurzinfo'
                        value={kurzinfo}
                        onChange={(e) => setKurzinfo(e.target.value)}
                        sx={{mb: 1}}
                    />
                </TableCell>
                <TableCell align='right'>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        <Button
                            variant='outlined'
                            color='error'
                            size='small'
                            startIcon={<Delete/>}
                            onClick={handleDelete}>
                            Löschen
                        </Button>
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            startIcon={<Save/>}
                            onClick={handleSave}>
                            Speichern
                        </Button>
                        <Button
                            variant='outlined'
                            color='secondary'
                            size='small'
                            startIcon={<Cancel/>}
                            onClick={onCancel}>
                            Abbrechen
                        </Button>
                    </Box>
                </TableCell>
            </TableRow>

            <Dialog
                open={deleteModalOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title" sx={{color: 'error.main'}}>
                    Zeiterfassung löschen
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description" sx={{mb: 2}}>
                        Sind Sie sicher, dass Sie diese Zeiterfassung löschen möchten?
                    </DialogContentText>
                    <Box sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                    }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1}}>
                            Details der Zeiterfassung:
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            <strong>Bauvorhaben:</strong> {arbeitszeiteintrag.BVH}
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            <strong>Datum:</strong> {arbeitszeiteintrag.DATUM}
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            <strong>Zeit:</strong> {arbeitszeiteintrag.BEGINN} - {arbeitszeiteintrag.ENDE}
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            <strong>Dauer:</strong> {arbeitszeiteintrag.DAUER.toFixed(2)} Stunden
                        </Typography>
                        {arbeitszeiteintrag.TAETIGKEIT && (
                            <Typography variant="body2" sx={{mb: 0.5}}>
                                <strong>Tätigkeit:</strong> {arbeitszeiteintrag.TAETIGKEIT}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="body2" color="error" sx={{mt: 2, fontWeight: 500}}>
                        Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        color="inherit"
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<Delete/>}
                        autoFocus
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditArbeitszeiteintrag;
